import { useLayoutEffect, useRef, useState } from "react";

const RESET_PATH = "/reset-password";
const TOKEN_MAX_LENGTH = 4096;
const SECURITY_METAS = [
  ["name", "robots", "noindex,nofollow,noarchive"],
  ["name", "referrer", "no-referrer"],
  ["http-equiv", "Cache-Control", "no-store, private, max-age=0"],
];

let capturedToken = null;

function readTokenFromFragment() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.slice(1));
  const tokens = params.getAll("token");
  const token = tokens.length === 1 ? tokens[0] : null;
  return token && token.length <= TOKEN_MAX_LENGTH && !/\s/.test(token) ? token : null;
}

function installSecurityMetas() {
  SECURITY_METAS.forEach(([attribute, value, content]) => {
    const selector = `meta[data-vmemoo-reset-security="${value}"]`;
    if (document.head.querySelector(selector)) return;
    const meta = document.createElement("meta");
    meta.setAttribute(attribute, value);
    meta.setAttribute("content", content);
    meta.setAttribute("data-vmemoo-reset-security", value);
    document.head.appendChild(meta);
  });
}

function removeSecurityMetas() {
  document.head.querySelectorAll("meta[data-vmemoo-reset-security]").forEach((meta) => meta.remove());
}

function captureToken({ force = false } = {}) {
  if (!force && capturedToken) return capturedToken;
  if (window.location.pathname !== RESET_PATH) return null;
  if (!window.location.hash) return capturedToken;
  capturedToken = readTokenFromFragment();
  if (window.location.hash) window.history.replaceState(null, "", RESET_PATH);
  installSecurityMetas();
  return capturedToken;
}

if (typeof window !== "undefined" && window.location.pathname === RESET_PATH) captureToken({ force: true });

export function __resetPasswordTokenCaptureForTests() {
  capturedToken = null;
  removeSecurityMetas();
}

export function PasswordResetPage() {
  const tokenRef = useRef(captureToken());
  const [handoffAttempted, setHandoffAttempted] = useState(false);
  const [, setCaptureVersion] = useState(0);
  const hasToken = Boolean(tokenRef.current);

  useLayoutEffect(() => {
    document.title = "Reset your Vmemoo password";
    if (window.location.hash) window.history.replaceState(null, "", RESET_PATH);
    installSecurityMetas();
    const captureNewFragment = () => {
      if (!window.location.hash) return;
      tokenRef.current = captureToken({ force: true });
      setHandoffAttempted(false);
      setCaptureVersion((version) => version + 1);
    };
    window.addEventListener("hashchange", captureNewFragment);
    return () => {
      window.removeEventListener("hashchange", captureNewFragment);
      removeSecurityMetas();
    };
  }, []);

  const openVmemoo = () => {
    const token = tokenRef.current;
    if (!token) return;
    const deepLink = `vmemoo://reset-password?token=${encodeURIComponent(token)}`;
    tokenRef.current = null;
    capturedToken = null;
    setHandoffAttempted(true);
    window.location.href = deepLink;
  };

  return <main className="reset-page" data-testid="password-reset-page">
    <div className="reset-card" data-testid="password-reset-card">
      <p className="eyebrow eyebrow-amber">Vmemoo · Password recovery</p>
      {hasToken ? (
        <div data-testid="password-reset-state-valid">
          <h1>Reset your Vmemoo password</h1>
          <p className="reset-body">Continue in the Vmemoo app to choose your new password.</p>
          <button type="button" className="button button-accent reset-cta" onClick={openVmemoo} data-testid="password-reset-open-app-button">Open Vmemoo</button>
          <p className="reset-security-note">For your security, password reset links expire after 30 minutes and can only be used once.</p>
        </div>
      ) : handoffAttempted ? (
        <div data-testid="password-reset-state-fallback">
          <h1>Continue in Vmemoo</h1>
          <p className="reset-body">You&apos;ll need the Vmemoo app to complete your password reset.</p>
          <p className="reset-security-note">For your security, this page cannot retry or display your reset link.</p>
          <a className="button button-outline-light reset-cta" href="/" data-testid="password-reset-back-home-link">Back to Vmemoo</a>
        </div>
      ) : (
        <div data-testid="password-reset-state-invalid">
          <h1>This password reset link is invalid or incomplete</h1>
          <p className="reset-body">Request a new password reset from the Vmemoo app.</p>
          <a className="button button-outline-light reset-cta" href="/" data-testid="password-reset-invalid-home-link">Back to Vmemoo</a>
        </div>
      )}
    </div>
  </main>;
}